import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useAppStore from "src/stores/appStore";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import collectionsService from "src/queries/services/collections";
import { queryClient } from "src/lib/react-query/queryClient";
import { CACHE_DURATION } from "@consts";
import { assistantIdSelector, systemModeSelector } from "src/stores/selectors/appSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { useDraft } from "src/queries/hooks/assistants";
import { toStoredKey } from "../ExploreTab.utils";

export { toStoredKey };

export const normalizeConfigRefOptions = (items, refInfo) => (items ?? [])
  .filter(({ display_key }) => display_key !== "")
  .map((item) => ({ ...item, key: toStoredKey(item.key, refInfo) }));

// Shared with fetchConfigRefs below so the hook and the imperative bulk
// download/upload path hit the same cache entry.
const configRefsQueryOptions = ({ selectedAssistantId, collectionId, queryParams, draftConfigId }) => ({
  queryKey: [FETCH_QUERY_KEYS.COLLECTION_REFS, collectionId, queryParams, draftConfigId ?? null],
  queryFn: async () => await collectionsService.fetchCollectionRefs({
    selectedAssistantId,
    collectionId,
    params: queryParams
  }),
  staleTime: CACHE_DURATION.ONE_MINUTE
});

// Returns the raw {live, draft} response, cache-backed while fresh.
export const fetchConfigRefs = (params) => queryClient.fetchQuery(configRefsQueryOptions(params));

const useConfigRefOptions = ({ scalarName, collectionId, queryParams }) => {
  const selectedAssistantId = useAppStore(assistantIdSelector);
  const systemMode = useAppStore(systemModeSelector);
  const { data: draft, isLoading: isDraftLoading } = useDraft();
  const hasDraft = !isDraftLoading && !!draft;
  const draftConfigId = draft?.configId;

  const { data, isLoading, isSuccess, isPlaceholderData } = useQuery({
    ...configRefsQueryOptions({ selectedAssistantId, collectionId, queryParams, draftConfigId }),
    placeholderData: keepPreviousData,
    enabled: !!selectedAssistantId && !!collectionId && !isDraftLoading
  });

  const useLive = systemMode === SYSTEM_MODES.READ_ONLY || !hasDraft || isPlaceholderData;
  const options = normalizeConfigRefOptions(useLive ? data?.live : data?.draft, { scalarName, collectionId });
  const labelByKey = options.reduce((acc, { key, display_key }) => {
    acc[key] = display_key;
    return acc;
  }, {});
  const keyByLastSegment = options.reduce((acc, { key }) => {
    const lastSegment = key.split("/").pop();
    if (!(lastSegment in acc)) acc[lastSegment] = key;
    return acc;
  }, {});

  const resolveKey = (value) => {
    if (!value || value in labelByKey) return value;
    return keyByLastSegment[value.split("/").pop()] ?? value;
  };

  return { options, isLoading, isSuccess, labelByKey, resolveKey };
};

export default useConfigRefOptions;
